// src/pages/Appointments/Admin/ActiveVisitsView.jsx
// OPTIONAL: Dedicated view for active visits
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiClock,
  FiUser,
  FiActivity,
  FiRefreshCw,
  FiEye,
} from "react-icons/fi";
import { getTodayAppointmentsThunk } from "../../../store/thunks/appointmentThunks";
import Button from "../../../components/common/Button";

const ActiveVisitsView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { todayAppointments, loadingList } = useSelector(
    (state) => state.appointment
  );
  const { selectedBranch } = useSelector((state) => state.branch);

  const [autoRefresh, setAutoRefresh] = useState(true);

  // Load today's appointments
  useEffect(() => {
    loadActiveVisits();
  }, [selectedBranch]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadActiveVisits();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, selectedBranch]);

  const loadActiveVisits = () => {
    const params = selectedBranch ? { branchCode: selectedBranch } : {};
    dispatch(getTodayAppointmentsThunk(params));
  };

  // Filter only active visits (CHECKED_IN or IN_PROGRESS)
  const activeVisits =
    todayAppointments?.filter(
      (apt) => apt.status === "CHECKED_IN" || apt.status === "IN_PROGRESS"
    ) || [];

  // Calculate visit durations
  const getVisitDuration = (appointment) => {
    if (appointment.visit?.checkInTime) {
      return Math.floor(
        (new Date() - new Date(appointment.visit.checkInTime)) / 60000
      );
    }
    return 0;
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text mb-1">
              Active Visits
            </h1>
            <p className="text-neutral-dark">
              Currently checked in or in progress ({activeVisits.length})
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadActiveVisits}
              disabled={loadingList}
            >
              <FiRefreshCw
                className={`mr-2 ${loadingList ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>

            <Button
              variant={autoRefresh ? "default" : "outline"}
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <FiClock className="mr-2" />
              Auto-refresh {autoRefresh ? "ON" : "OFF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Active Visits Grid */}
      {loadingList && activeVisits.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : activeVisits.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FiActivity className="mx-auto text-6xl text-neutral-dark mb-4" />
          <h3 className="text-xl font-semibold text-text mb-2">
            No Active Visits
          </h3>
          <p className="text-neutral-dark mb-4">
            No customers are currently checked in
          </p>
          <Button onClick={() => navigate("/appointments/today")}>
            View All Today's Appointments
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeVisits.map((appointment) => (
            <ActiveVisitCard
              key={appointment._id}
              appointment={appointment}
              duration={getVisitDuration(appointment)}
              onView={() => navigate(`/appointments/${appointment._id}`)}
            />
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {activeVisits.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Checked In"
            value={activeVisits.filter((a) => a.status === "CHECKED_IN").length}
            color="blue"
          />
          <StatCard
            label="In Progress"
            value={
              activeVisits.filter((a) => a.status === "IN_PROGRESS").length
            }
            color="purple"
          />
          <StatCard
            label="Avg Duration"
            value={
              activeVisits.length > 0
                ? Math.round(
                    activeVisits.reduce(
                      (sum, a) => sum + getVisitDuration(a),
                      0
                    ) / activeVisits.length
                  ) + " min"
                : "0 min"
            }
            color="green"
          />
          <StatCard
            label="Total Activities"
            value={activeVisits.reduce(
              (sum, a) =>
                sum +
                (a.visit?.activities?.productsViewed?.length || 0) +
                (a.visit?.activities?.productsTriedOn?.length || 0),
              0
            )}
            color="orange"
          />
        </div>
      )}
    </div>
  );
};

// Active Visit Card Component
const ActiveVisitCard = ({ appointment, duration, onView }) => {
  const isLongVisit = duration > appointment.duration + 15;
  const isNearScheduled = duration > appointment.duration;

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
      <div className="p-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-3">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              appointment.status === "CHECKED_IN"
                ? "bg-blue-100 text-blue-800"
                : "bg-purple-100 text-purple-800"
            }`}
          >
            {appointment.status === "CHECKED_IN" ? "Checked In" : "In Progress"}
          </span>

          {/* Duration Badge */}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              isLongVisit
                ? "bg-red-100 text-red-800"
                : isNearScheduled
                ? "bg-yellow-100 text-yellow-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {duration} min
          </span>
        </div>

        {/* Customer Info */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2">
            <FiUser className="text-neutral-dark" />
            <h3 className="font-semibold text-text">{appointment.userName}</h3>
          </div>
          <p className="text-sm text-neutral-dark">{appointment.userPhone}</p>
        </div>

        {/* Time Info */}
        <div className="flex items-center gap-2 mb-3 text-sm text-neutral-dark">
          <FiClock size={14} />
          <span>
            Started:{" "}
            {appointment.visit?.checkInTime &&
              new Date(appointment.visit.checkInTime).toLocaleTimeString(
                "en-IN",
                {
                  hour: "2-digit",
                  minute: "2-digit",
                }
              )}
          </span>
        </div>

        {/* Activities */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-1">
            <FiEye className="text-blue-600" size={14} />
            <span className="text-neutral-dark">
              {appointment.visit?.activities?.productsViewed?.length || 0}{" "}
              viewed
            </span>
          </div>
          <div className="flex items-center gap-1">
            <FiActivity className="text-purple-600" size={14} />
            <span className="text-neutral-dark">
              {appointment.visit?.activities?.productsTriedOn?.length || 0}{" "}
              tried
            </span>
          </div>
        </div>

        {/* Action Button */}
        <Button onClick={onView} variant="outline" className="w-full">
          <FiEye className="mr-2" />
          View & Track
        </Button>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, color }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <p className="text-sm text-neutral-dark mb-1">{label}</p>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

export default ActiveVisitsView;
